import React, { useEffect, useCallback, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';

import {
  useImageExperienceAdPreviewModel,
  AdPreviewSinglePlacementLayout,
  PlacementType2AssociationTypeMap,
} from '@bingads-webui-campaign-react/unified-ad-preview-library-cmui';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';

import { PreviewPlacementContainer } from './preview-placement-container';
import { normalizePreviewTexts } from './util';

export const UnifiedAdPreviewSinglePlacementWrapper = ({
  i18n,
  placement,
  previewTexts,
  previewLogo,
  adPreviewConfig,
  placementType,
  isMultimediaAd,
  isMultiMediaAdsPhase2Enabled,
}) => {
  const { adType, placementConfig = [], extraPlacementInfo = {} } = adPreviewConfig;
  const placementTypesList = useMemo(() => _.chain(placementConfig).map(({ placementTypes }) => placementTypes).flatten().value(), [placementConfig]);

  const placementAlias = useMemo(() => {
    if (placementType) {
      return placementType;
    }

    // this is a temporary solution to get the placement type for MMA and MSAN.
    // We can pass placementType from parent component when old preview caousel is updated by UAPL preview carousel.
    let alias = '';
    _.map(placementTypesList, (placementTypeInner) => {
      const associationType = PlacementType2AssociationTypeMap[placementTypeInner];
      if (placement && placement.associationType === associationType) {
        alias = placementTypeInner;
      } else if (placement && placement.type && constants.subTypeToType[associationType] === placement.type) {
        alias = placementTypeInner;
        placement.setAssociationType(associationType);
      }
    });
    return alias;
  }, [placement, placementType, placementTypesList]);

  const placementConfigInner = useMemo(() => [
    {
      placementSetName: adType,
      placementTypes: [placementAlias],
    },
  ], [adType, placementAlias]);

  const {
    firstImage,
    multiImagePlacementSets: placementSets,
    onUpdateImageExperienceImageList,
  } = useImageExperienceAdPreviewModel(placementConfigInner);

  useEffect(() => {
    onUpdateImageExperienceImageList([[placement]]);
  }, [placement, onUpdateImageExperienceImageList]);

  const onRenderPlacement = useCallback((pl, AdPlacementComponent) => {
    const { placementMeta } = pl;
    const { imageMeta } = firstImage;

    if (!placementMeta || !imageMeta) {
      return null;
    }

    const { placementType: placementTypeInner } = placementMeta;
    const unifiedPreviewTexts = normalizePreviewTexts(previewTexts, placementTypeInner);

    return (
      <PreviewPlacementContainer
        i18n={i18n}
        adType={adType}
        placementType={placementTypeInner}
      >
        <AdPlacementComponent
          placementData={pl}
          i18n={i18n}
          imagePreviewHeight={extraPlacementInfo[placementTypeInner] && extraPlacementInfo[placementTypeInner].imagePreviewHeight}
          customClassName={extraPlacementInfo[placementTypeInner] && extraPlacementInfo[placementTypeInner].customClassName}
          {...unifiedPreviewTexts}
          logo={previewLogo}
          displayFinalUrlOrigin={isMultimediaAd && isMultiMediaAdsPhase2Enabled}
        />
      </PreviewPlacementContainer>
    );
  }, [adType, extraPlacementInfo, firstImage, i18n, isMultiMediaAdsPhase2Enabled, isMultimediaAd, previewLogo, previewTexts]);

  const placementList = useMemo(() => (!_.isEmpty(placementSets) && placementSets[0] && placementSets[0].placementList) || [], [placementSets]);

  const placementData = useMemo(() => _.find(placementList, ({ placementMeta }) => placementMeta !== undefined), [placementList]);

  return (
    <AdPreviewSinglePlacementLayout
      placementData={placementData}
      onRenderPlacement={onRenderPlacement}
    />
  );
};

UnifiedAdPreviewSinglePlacementWrapper.propTypes = {
  adPreviewConfig: PropTypes.shape({
    adType: PropTypes.string,
    placementConfig: PropTypes.arrayOf(PropTypes.shape({
      placementSetName: PropTypes.string,
      placementTypes: PropTypes.arrayOf(PropTypes.string),
    })),
    extraPlacementInfo: PropTypes.shape({}),
  }),
  placement: PropTypes.shape({
    associationType: PropTypes.string,
    type: PropTypes.string,
    setAssociationType: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  previewTexts: PropTypes.shape({
    finalUrl: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  previewLogo: PropTypes.shape({}),
  placementType: PropTypes.string,
  isMultimediaAd: PropTypes.bool,
  isMultiMediaAdsPhase2Enabled: PropTypes.bool,
};

UnifiedAdPreviewSinglePlacementWrapper.defaultProps = {
  adPreviewConfig: {
    adType: '',
    placementConfig: [],
  },
  placementType: '',
  previewLogo: undefined,
  isMultimediaAd: false,
  isMultiMediaAdsPhase2Enabled: false,
};
